import { isAxiosError, isCancel } from 'axios';
import { Result, err, ok } from 'neverthrow';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import axios from '@utils/axios';

import { ApiError } from 'types/api/ApiError';
import captureException from 'utils/captureException';

export type ResolveAddressRequest = {
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
};

export type ResolvedAddress = {
  city: string;
  state: string;
  zip: string;
  country: string;
};

type ResolveAddressResponse = {
  address: ResolvedAddress;
};

/**
 * Resolves partial address details into a full address via the BE
 * `/locations/resolve` endpoint. Pass any combination of city, state, zip, and
 * country; the backend fills in whatever is missing.
 *
 * Currently used to resolve missing US state codes from Transporeon data, but
 * intentionally generic so other portals can call it without changes here.
 */
export async function resolveAddress(
  request: ResolveAddressRequest
): Promise<Result<ResolvedAddress, ApiError>> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 500); // 0.5 second timeout

  try {
    const response = await axios.post<ResolveAddressResponse>(
      '/locations/resolve',
      request,
      {
        signal: controller.signal,
      }
    );

    clearTimeout(timeoutId);
    return ok(response.data.address);
  } catch (error) {
    clearTimeout(timeoutId);
    captureException(error, { functionName: 'resolveAddress' });

    if (isCancel(error)) {
      return err({ message: 'Request timed out after 0.5 seconds' });
    }

    if (!isAxiosError(error)) {
      return err({ message: 'Failed to resolve address' });
    }

    return err({
      message: error.response?.data?.message || 'Failed to resolve address',
      status: error.response?.status,
    });
  }
}
